import { useIsMobile } from "@/hooks/use-mobile";
import {
  actionsKeys,
  cn,
  formatAmount,
  getRequiredFields,
  invoiceParkStatus,
  permissionActions,
  toApiSort,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useDeleteParkDocMutation, useGetBillingCategoryByCommunity, useGetBillingLogsList } from "@/services";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  parkDocumentSearch,
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  searchType,
  transactionTypeValues,
} from "../../consts";
import { Badge, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { Edit, MoreVertical, Trash2 } from "lucide-react";

const useListing = () => {
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "billingLogId", desc: true }]);
  const [month, setMonth] = useState<number | string>("all");
  const [monthKey] = useState<number>(Math.random() * 2);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const selectedRow = useRef<any>(null);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const { data: billingCategory, isLoading: isLoadingBillingCategory } =
  useGetBillingCategoryByCommunity({communityId: communityDetail?.communityId});

  const hasUpdatePermission = useMemo(() => {
    return accessPermission?.[actionsKeys.BILLING_DOCUMENT?.toLowerCase()]?.[permissionActions.UPDATE];
  }, [accessPermission]);

  const hasDeletePermission = useMemo(() => {
    return accessPermission?.[actionsKeys.BILLING_DOCUMENT?.toLowerCase()]?.[permissionActions.DELETE];
  }, [accessPermission]);
  
  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetBillingLogsList({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "billingYear",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        ...(month != "all"
          ? [
              {
                field: "billingPeriod",
                value: month,
                operator: "EQUAL",
              },
            ]
          : []),
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "parkDocumentHeaderStatus",
          value: "DRAFT",
          operator: "EQUAL",
        },
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, parkDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "billingLogId", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchDoc();
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchDoc(), 100);
  };

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: "",
        name: <span className="text-primary">Pending for Posting</span>,
      },
    ],
    []
  );

  const { mutate: deleteDocument, isPending: isDeleteDocumentPending } =
    useDeleteParkDocMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Document Deleted Successfully");
        setOpenDeleteDialog(false);
        queryClient.invalidateQueries({
          queryKey: ["billing-logs"],
        });
      },
    });
    
  // Define Table Columns
  const columns = [
    {
      accessorKey: "billingLogId",
      header: <div>Job ID</div>,
      accessorFn: (row: any) => String(row?.billingLogId || ""),
    },
    {
      accessorKey: "parkedDocumentHeaderId",
      header: (
        <div>
          Parked <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row?.parkedDocumentHeaderId || ""),
    },
    {
      accessorKey: "transactionType",
      header: <div>Transaction Type</div>,
      accessorFn: (row: any) =>
        (transactionTypeValues as any)[String(row?.transactionType || "")],
    },
    {
      accessorKey: "billingCategoryIds",
      header: <div>Billing Category</div>,
      accessorFn: (row: any) =>
        row.billingCategoryIds?.split(",")?.map((data: any) => {
          return (
            billingCategory?.data?.find(
              (val: any) =>
                data?.toString() == val.billingCategoryId?.toString()
            )?.categoryName || ""
          );
        }),
    },
    {
      accessorKey: "billingRunDate",
      header: <div>Billing Date</div>,
      accessorFn: (row: any) =>
        String(row?.transactionType || "")?.toUpperCase() ==
          "MONTHLY_HOA_FEE" && row?.createdAt
          ? moment(row?.createdAt || "").format("MM/DD/YYYY")
          : row?.billingRunDate
          ? moment(row?.billingRunDate || "").format("MM/DD/YYYY")
          : "",
    },
    {
      accessorKey: "amount",
      meta: { align: "right" },
      header: <div>Billing Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.amount || "0"),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: () => (
        <Badge
          variant="outline"
          className={cn(
            invoiceParkStatus["draft"]?.color,
            "rounded-full px-4 py-2"
          )}
        >
          {invoiceParkStatus["draft"]?.label}
        </Badge>
      ),
    },
    ...(hasUpdatePermission || hasDeletePermission) ? [{
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({row}: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              {hasUpdatePermission && <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`${row.original.parkedDocumentHeaderId}/edit`);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>}
              {hasDeletePermission && <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectedRow.current = row.original;
                  setOpenDeleteDialog(true);
                }}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    }] : [],
  ];

  const onRowClick = (row: any) => {
    navigate(`${row.original.parkedDocumentHeaderId}/view`);
  };

  const onMonthChange = (val: any) => {
    setMonth(val);
    setTimeout(() => {
      refetchDoc();
    }, 100);
  };

  const onDeleteConfirm = () => {
    deleteDocument({
      communityId: communityDetail?.communityId,
      parkDocId: selectedRow.current.parkedDocumentHeaderId,
    });
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    form,
    requiredFields,
    onSubmit,
    onClear,
    communityDetail,
    onMonthChange,
    monthKey,
    month,
    isLoadingBillingCategory,
    billingCategory,
    currentFinanceYear,
    openDeleteDialog,
    setOpenDeleteDialog,
    isDeleteDocumentPending,
    onDeleteConfirm,
    selectedRow: selectedRow.current,
    sorting,
    setSorting,
  };
};

export default useListing;
